import { LitElement, html, css } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import type { Feature } from '../../schemas/feature.js'

@customElement('editing-tools-bar')
export class EditingToolsBar extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      align-items: stretch;
      width: 100%;
      height: 100%;
    }

    .back-button {
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.85);
      cursor: pointer;
      padding: 0 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.15s ease;
    }

    .back-button:hover {
      background: rgba(255, 255, 255, 0.12);
    }

    .name-button {
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.85);
      cursor: pointer;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      gap: 2px;
      transition: all 0.15s ease;
      padding: 0 12px;
      height: 100%;
      white-space: nowrap;
      font-weight: 600;
    }

    .name-title {
      font-size: 12px;
      font-weight: 600;
      line-height: 1;
    }

    .name-value {
      font-size: 13px;
      font-weight: 400;
      opacity: 0.7;
      line-height: 1;
    }

    .name-button:hover {
      color: white;
      background: rgba(255, 255, 255, 0.12);
    }

    .name-button.active {
      color: white;
      background: #5a7088;
    }

    .tools-group {
      display: flex;
      height: 100%;
    }

    .tool-button {
      width: 56px;
      height: 100%;
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.85);
      cursor: pointer;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 2px;
      transition: all 0.15s ease;
      padding: 0;
    }

    .tool-button:hover {
      color: white;
      background: rgba(255, 255, 255, 0.12);
    }

    .tool-button.active {
      color: white;
      background: #5a7088;
    }

    .tool-button:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    .tool-label {
      font-size: 10px;
      font-weight: 500;
      line-height: 1;
    }

    .material-icons {
      font-family: 'Material Symbols Outlined';
      font-size: 20px;
    }
  `

  @property({ type: Array })
  selectedFeatures: Feature[] = []

  @property({ type: Boolean })
  splitEnabled = false

  @property({ type: String })
  activeStyleProperty?: string

  /** When true, this bar is editing an in-progress (mid-draw) draft: show Undo +
   *  Finish/Cancel and (for lines) the snap toggles instead of Extend/Split/Delete. */
  @property({ type: Boolean })
  creating = false

  /** The draw tool being created (line/polygon/point/...) — used to render the
   *  create bar before the first point exists (no draft feature yet). */
  @property({ type: String })
  creatingTool?: string

  @property({ type: Boolean })
  snapTrails = false

  @property({ type: Boolean })
  snapTerrain = false

  private getShapeName(featureType: string): string {
    switch (featureType) {
      case 'point':
        return 'Point'
      case 'line':
        return 'Line'
      case 'polygon':
        return 'Polygon'
      case 'circle':
        return 'Circle'
      case 'rectangle':
        return 'Rectangle'
      case 'measure':
        return 'Measurement'
      default:
        return 'Shape'
    }
  }

  private getFeatureLabel(feature: Feature): string {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty name/text should fall through
    return feature.name || feature.label?.text || this.getShapeName(feature.type)
  }

  private deleteFeatures(): void {
    this.selectedFeatures.forEach(feature => {
      this.dispatchEvent(
        new CustomEvent('feature-deleted', {
          detail: { featureId: feature.id },
          bubbles: true,
        })
      )
    })
  }

  private extendFeature(): void {
    this.dispatchEvent(
      new CustomEvent('feature-extend', {
        detail: { feature: this.selectedFeatures[0] },
        bubbles: true,
      })
    )
  }

  private splitFeature(): void {
    this.dispatchEvent(
      new CustomEvent('feature-split', {
        detail: { feature: this.selectedFeatures[0] },
        bubbles: true,
      })
    )
  }

  private undoPoint(): void {
    this.dispatchEvent(new CustomEvent('create-undo', { bubbles: true }))
  }

  private redoPoint(): void {
    this.dispatchEvent(new CustomEvent('create-redo', { bubbles: true }))
  }

  private outAndBack(): void {
    this.dispatchEvent(new CustomEvent('create-out-and-back', { bubbles: true }))
  }

  private returnHome(): void {
    this.dispatchEvent(new CustomEvent('create-return', { bubbles: true }))
  }

  /** Out-and-back + Return (best route) — the two finish-as-loop options in extend. */
  private renderReturnActions(): unknown {
    return html`
      <button class="tool-button" title="Out and back (retrace path)" @click=${this.outAndBack}>
        <span class="material-icons">compare_arrows</span>
        <span class="tool-label">Out &amp; back</span>
      </button>
      <button class="tool-button" title="Return by best route" @click=${this.returnHome}>
        <span class="material-icons">u_turn_left</span>
        <span class="tool-label">Return</span>
      </button>
    `
  }

  /** Undo + Redo, shown while drawing a line/polygon. */
  private renderCreateUndoRedo(type: string): unknown {
    const canUndo = type === 'line' || type === 'track' || type === 'route' || type === 'polygon'
    if (!canUndo) return ''
    return html`
      <button class="tool-button" @click=${this.undoPoint} title="Undo">
        <span class="material-icons">undo</span>
        <span class="tool-label">Undo</span>
      </button>
      <button class="tool-button" @click=${this.redoPoint} title="Redo">
        <span class="material-icons">redo</span>
        <span class="tool-label">Redo</span>
      </button>
    `
  }

  private finishCreate(): void {
    this.dispatchEvent(new CustomEvent('create-finish', { bubbles: true }))
  }

  private cancelCreate(): void {
    this.dispatchEvent(new CustomEvent('create-cancel', { bubbles: true }))
  }

  private renderSnapToggles(): unknown {
    return html`
      <button
        class="tool-button ${this.snapTrails ? 'active' : ''}"
        title="Snap to trails"
        @click=${(): void => this.toggleSnap('trail')}
      >
        <span class="material-icons">route</span>
        <span class="tool-label">Trail</span>
      </button>
      <button
        class="tool-button ${this.snapTerrain ? 'active' : ''}"
        title="Follow terrain (DEM)"
        @click=${(): void => this.toggleSnap('terrain')}
      >
        <span class="material-icons">terrain</span>
        <span class="tool-label">Terrain</span>
      </button>
    `
  }

  /** Create bar before any draft feature exists (tool selected, no points yet). */
  private renderCreatingNoFeature(): unknown {
    const type = this.creatingTool ?? 'line'
    const isLine = type === 'line' || type === 'track' || type === 'route' || type === 'measure'
    return html`
      <button class="back-button" @click=${this.cancelCreate} title="Cancel">
        <span class="material-icons">arrow_back</span>
      </button>
      <button class="name-button" disabled title="New ${this.getShapeName(type)}">
        <span class="name-title">New</span>
        <span class="name-value">${this.getShapeName(type)}</span>
      </button>
      <div class="tools-group">
        ${isLine ? this.renderSnapToggles() : ''} ${this.renderCreateUndoRedo(type)}
        <button class="tool-button" disabled title="Finish (place more points)">
          <span class="material-icons">check</span>
          <span class="tool-label">Finish</span>
        </button>
        <button class="tool-button" @click=${this.cancelCreate} title="Cancel">
          <span class="material-icons">close</span>
          <span class="tool-label">Cancel</span>
        </button>
      </div>
    `
  }

  private toggleSnap(which: 'trail' | 'terrain'): void {
    this.dispatchEvent(
      new CustomEvent('snap-toggled', {
        detail: {
          trail: which === 'trail' ? !this.snapTrails : this.snapTrails,
          terrain: which === 'terrain' ? !this.snapTerrain : this.snapTerrain,
        },
        bubbles: true,
      })
    )
  }

  private openStyleEditor(styleProperty: string, e: Event): void {
    const feature = this.selectedFeatures[0] as Feature | undefined
    if (!feature) {
      return
    }
    const button = e.currentTarget as HTMLElement
    const rect = button.getBoundingClientRect()
    this.dispatchEvent(
      new CustomEvent('open-style-editor', {
        detail: { featureId: feature.id, styleProperty, feature, buttonRect: rect },
        bubbles: true,
      })
    )
  }

  private renderToolButtons(feature: Feature): unknown[] {
    const type = feature.type
    const buttons = []

    if (type === 'point') {
      buttons.push(html`
        <button
          class="tool-button ${this.activeStyleProperty === 'marker' ? 'active' : ''}"
          title="Marker"
          @click=${(e: Event): void => this.openStyleEditor('marker', e)}
        >
          <span class="material-icons">place</span>
          <span class="tool-label">Marker</span>
        </button>
        <button
          class="tool-button ${this.activeStyleProperty === 'stroke' ? 'active' : ''}"
          title="Stroke"
          @click=${(e: Event): void => this.openStyleEditor('stroke', e)}
        >
          <span class="material-icons">line_style</span>
          <span class="tool-label">Stroke</span>
        </button>
        <button
          class="tool-button ${this.activeStyleProperty === 'fillColor' ? 'active' : ''}"
          title="Fill"
          @click=${(e: Event): void => this.openStyleEditor('fillColor', e)}
        >
          <span class="material-icons">format_color_fill</span>
          <span class="tool-label">Fill</span>
        </button>
      `)
    } else if (type === 'line' || type === 'track' || type === 'route') {
      if (this.creating) {
        // Create face: snap toggles + the two returns. No stroke/tags/layer here.
        // Out-and-back / return-home are loop-route actions — meaningless for an
        // ephemeral measurement, so a measure shows only the snap toggles.
        buttons.push(this.renderSnapToggles())
        if (this.creatingTool !== 'measure') buttons.push(this.renderReturnActions())
      } else {
        buttons.push(html`
          <button
            class="tool-button ${this.activeStyleProperty === 'lineStroke' ? 'active' : ''}"
            title="Stroke"
            @click=${(e: Event): void => this.openStyleEditor('lineStroke', e)}
          >
            <span class="material-icons">line_style</span>
            <span class="tool-label">Stroke</span>
          </button>
        `)
      }
    } else {
      buttons.push(html`
        <button
          class="tool-button ${this.activeStyleProperty === 'stroke' ? 'active' : ''}"
          title="Stroke"
          @click=${(e: Event): void => this.openStyleEditor('stroke', e)}
        >
          <span class="material-icons">line_style</span>
          <span class="tool-label">Stroke</span>
        </button>
        <button
          class="tool-button ${this.activeStyleProperty === 'fillColor' ? 'active' : ''}"
          title="Fill"
          @click=${(e: Event): void => this.openStyleEditor('fillColor', e)}
        >
          <span class="material-icons">format_color_fill</span>
          <span class="tool-label">Fill</span>
        </button>
      `)
    }

    // Layer: only when editing a committed feature — not while drawing. (Tags are
    // stored but nothing consumes them yet, so the editor is omitted for now.)
    if (!this.creating) {
      buttons.push(html`
        <button
          class="tool-button ${this.activeStyleProperty === 'layer' ? 'active' : ''}"
          title="Layer"
          @click=${(e: Event): void => this.openStyleEditor('layer', e)}
        >
          <span class="material-icons">layers</span>
          <span class="tool-label">Layer</span>
        </button>
      `)
    }

    if (this.creating) {
      buttons.push(this.renderCreateUndoRedo(type))
      buttons.push(html`
        <button class="tool-button" @click=${this.cancelCreate} title="Cancel">
          <span class="material-icons">close</span>
          <span class="tool-label">Cancel</span>
        </button>
      `)
    } else {
      const isLine = type === 'line' || type === 'track' || type === 'route'
      buttons.push(html`
        ${isLine
          ? html`<button class="tool-button" title="Extend line" @click=${this.extendFeature}>
              <span class="material-icons">add</span>
              <span class="tool-label">Extend</span>
            </button>`
          : ''}
        ${isLine
          ? html`<button
              class="tool-button"
              title="Split line at selected vertex"
              ?disabled=${!this.splitEnabled}
              @click=${this.splitFeature}
            >
              <span class="material-icons">content_cut</span>
              <span class="tool-label">Split</span>
            </button>`
          : ''}
        <button class="tool-button" @click=${this.deleteFeatures} title="Delete">
          <span class="material-icons">delete</span>
          <span class="tool-label">Delete</span>
        </button>
        <button class="tool-button" @click=${this.cancelEdit} title="Cancel">
          <span class="material-icons">close</span>
          <span class="tool-label">Cancel</span>
        </button>
      `)
    }

    return buttons
  }

  private saveEdit(): void {
    this.dispatchEvent(new CustomEvent('edit-saved', { bubbles: true }))
  }

  private cancelEdit(): void {
    this.dispatchEvent(new CustomEvent('edit-cancelled', { bubbles: true }))
  }

  override render(): unknown {
    const feature = this.selectedFeatures[0] as Feature | undefined
    if (!feature) {
      return this.creating ? this.renderCreatingNoFeature() : html``
    }

    return html`
      <button
        class="back-button"
        @click=${this.creating ? this.finishCreate : this.saveEdit}
        title=${this.creating ? 'Finish' : 'Save'}
      >
        <span class="material-icons">check</span>
      </button>

      ${this.creating
        ? ''
        : html`<button
            class="name-button ${this.activeStyleProperty === 'label' ? 'active' : ''}"
            @click=${(e: Event): void => this.openStyleEditor('label', e)}
            title="Edit label"
          >
            <span class="name-title">Label</span>
            <span class="name-value">${this.getFeatureLabel(feature)}</span>
          </button>`}

      <div class="tools-group">${this.renderToolButtons(feature)}</div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'editing-tools-bar': EditingToolsBar
  }
}
